/** Where the guided setup screens live (#1742): the step-by-step path, one click from the setup conversation. */
export const GUIDED_SETUP_PATH = '/onboarding/guided'

/** The steps the guided step bar shows, in setup's order (#1742). */
export const GUIDED_STEPS = [
    { id: 'work', label: 'Kind of work' },
    { id: 'about_you', label: 'About you' },
    { id: 'team', label: 'Your team' },
    { id: 'names', label: 'Names' },
    { id: 'team_setup', label: 'Team setup' },
] as const

export type GuidedStepId = (typeof GUIDED_STEPS)[number]['id']

/** The guided screen a setup step is shown on: its own screen, or a hand-over to the COO for a step with none yet. */
export type GuidedScreen = 'ai_account' | 'work' | 'about_you' | 'team' | 'names' | 'team_setup' | 'hand_over' | 'done'

/** The setup steps with no guided screen of their own yet, by the title the shell shows for each (#1742). */
export const HAND_OVER_TITLES: Readonly<Record<string, string>> = {
    first_approval: 'Your first approval',
}

/**
 * The guided screen for the server's current step, and the step bar's mark: the work profile is two screens, Kind of
 * work until a kind is saved and About you after it. `team_proposal` is the Your team screen and `names` the Names
 * screen (#2216). `assembly` is shown on the team-setup screen (#1743, guided screen 5), whose assembly log comes
 * first, above its tool rows. A step with no guided screen yet hands over to the COO with its title, so setup never
 * shows a blank page. Null (setup complete) is done.
 */
export function guidedScreenAt(
    currentStep: string | null,
    savedProfile: unknown,
): { screen: GuidedScreen; bar: GuidedStepId | null } {
    if (currentStep === null) {
        return { screen: 'done', bar: null }
    }
    if (currentStep === 'ai_account') {
        return { screen: 'ai_account', bar: null }
    }
    if (currentStep === 'work_profile') {
        const workSaved = isRecord(savedProfile) && isRecord(savedProfile['work'])
        return workSaved ? { screen: 'about_you', bar: 'about_you' } : { screen: 'work', bar: 'work' }
    }
    if (currentStep === 'team_proposal') {
        return { screen: 'team', bar: 'team' }
    }
    if (currentStep === 'names') {
        return { screen: 'names', bar: 'names' }
    }
    if (currentStep === 'assembly' || currentStep === 'team_setup') {
        return { screen: 'team_setup', bar: 'team_setup' }
    }
    const bar: Readonly<Record<string, GuidedStepId>> = { first_approval: 'team_setup' }
    return { screen: 'hand_over', bar: bar[currentStep] ?? null }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}
